/**
 * Tracking utility exports
 *
 * All components should import tracking functionality from this file. This will allow
 * future updates to underlying implementations for tracking to be modified without updating n files.
 *
 * Ideally we can just replace the underlying implementation here and it should reflect throughout the application.
 */

import WebAnalytics, { AnalyticsDestination, CustomerType, Environment, EventCategory } from '@lendi/analytics-web';
import { Brand } from '@lendi/lala-utils';

export class Analytics {
  private eventCategory: EventCategory;
  private customerType: CustomerType = CustomerType.CUSTOMER;
  private instance: WebAnalytics | null = null;
  private _isReady = false;

  constructor(destination: AnalyticsDestination, environment: Environment, brand: Brand) {
    this.instance = new WebAnalytics({ analyticsDestination: destination, environment, brand });
    this.eventCategory = EventCategory.CREDIT_SCORE;
    this._isReady = true;
  }

  public isReady(): boolean {
    return this._isReady;
  }

  public setEventCategory(category: EventCategory): void {
    this.eventCategory = category;
  }

  public setCustomerType(type: CustomerType): void {
    this.customerType = type;
  }

  public trackEvent(event: any, withFlush = false): unknown {
    if (!this.instance || !this.isReady()) {
      throw new Error('Analytics instance is not initialized');
    }
    return this.instance.trackEvent(
      {
        ...event,
        customerType: this.customerType,
      },
      withFlush
    ) as unknown;
  }

  public trackPage(name: string, properties?: Record<string, string>): unknown {
    if (!this.instance || !this.isReady()) {
      throw new Error('Analytics instance is not initialized');
    }
    return this.instance.trackPage(this.eventCategory, name, {
      ...properties,
      customerType: this.customerType,
    }) as unknown;
  }

  public identify(userId: string, flags: Record<string, unknown>, traits?: Record<string, unknown>): unknown {
    if (!this.instance || !this.isReady()) {
      throw new Error('Analytics instance is not initialized');
    }
    return this.instance.identify(userId, flags, {
      ...traits,
      customerType: this.customerType,
    });
  }
}
